import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { formatYuan } from '../utils/money';
import { getStatusText, getStatusColor, QUOTATION_STATUS_LIST } from '../utils/quotationStatus';

export default function QuotationList() {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/quotations');
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setError(error.response?.data?.error || error.message || '加载报价单列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/quotations/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchQuotations();
    } catch (error) {
      console.error('Error deleting quotation:', error);
    }
  };

  const filteredQuotations =
    statusFilter === 'ALL'
      ? quotations
      : quotations.filter((q) => q.status === statusFilter);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>报价单</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchQuotations}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">报价单</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/quotations/new')}
            sx={{ mr: 1 }}
          >
            新建报价
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
        {QUOTATION_STATUS_LIST.map(({ value, label }) => {
          const status = value || 'ALL';
          return (
            <Chip
              key={status}
              label={status === 'ALL' ? '全部' : label}
              onClick={() => setStatusFilter(status)}
              color={statusFilter === status ? 'primary' : 'default'}
              variant={statusFilter === status ? 'filled' : 'outlined'}
            />
          );
        })}
      </Box>

      {filteredQuotations.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无报价单
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary">
              点击"新建报价"创建第一个报价单
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredQuotations.map((quotation) => (
            <Grid item xs={12} sm={6} md={4} key={quotation.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'grey.300' },
                }}
                onClick={() => navigate(`/quotations/${quotation.id}`)}
              >
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" gutterBottom>
                      {quotation.projectName}
                    </Typography>
                    <Chip
                      label={getStatusText(quotation.status)}
                      color={getStatusColor(quotation.status)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    建设单位：{quotation.buildUnit}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    报价日期：{quotation.quoteDate}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                    {formatYuan(quotation.totalAmount)}
                  </Typography>
                  <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(e, quotation.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定要删除这个报价单吗？"
      />
    </Box>
  );
}
